import { clearAttributionCookie } from "./acquisitionAttribution.js";

export const CONSENT_COOKIE = "__Secure-ap_attribution_consent_v1";
export const CONSENT_STORAGE_KEY = "ap_attribution_consent_v1";
export const CONSENT_MAX_AGE = 90 * 24 * 60 * 60;
export const NOTICE_VERSION = 2;

export function validateConsent(value, now = Date.now()) {
  if (!value || value.version !== 1 ||
      !(value.notice_version === NOTICE_VERSION || (value.notice_version === 1 && value.decision === "denied")) ||
      !["granted", "denied"].includes(value.decision) || typeof value.decided_at !== "string") return null;
  const time = Date.parse(value.decided_at);
  if (!Number.isFinite(time) || time > now + 300000 || time <= now - CONSENT_MAX_AGE * 1000) return null;
  return { version: 1, notice_version: value.notice_version, decision: value.decision, decided_at: new Date(time).toISOString() };
}

export function createConsentAdapter(target = globalThis.window, clear = clearAttributionCookie, { now = () => Date.now() } = {}) {
  const listeners = new Set();
  let channel;
  let refreshing = false;
  let memoryOnly = false;
  let disposed = false;
  const local = () => ["localhost", "127.0.0.1", "[::1]"].includes(target?.location?.hostname);
  function readStored() {
    try {
      if (local()) return { available: true, value: target.localStorage.getItem(CONSENT_STORAGE_KEY) };
      if (!target?.document) return { available: false, value: null };
      const prefix = CONSENT_COOKIE + "=";
      const cookie = target.document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(prefix));
      return { available: true, value: cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null };
    } catch { return { available: false, value: null }; }
  }
  function parse(raw) {
    try { return validateConsent(JSON.parse(raw), now()); } catch { return null; }
  }
  let preference = parse(readStored().value);
  // Compatibility with an approved platform that initializes before this adapter.
  if (!preference && target?.APAttributionConsentGranted === true && target?.APAttributionConsentNoticeVersion === NOTICE_VERSION) {
    preference = { version: 1, notice_version: NOTICE_VERSION, decision: "granted", decided_at: new Date(now()).toISOString() };
    memoryOnly = true;
  }

  function publish(next) {
    const previous = preference?.decision || "unknown";
    preference = next;
    const state = next?.decision || "unknown";
    if (target) target.APAttributionConsentGranted = state === "granted";
    if (previous !== state) {
      if (state !== "granted") {
        try { clear(); } catch { /* Preference updates must not interrupt signup. */ }
      }
      for (const listener of listeners) listener(state === "granted", state);
    }
  }

  function refresh() {
    if (refreshing || disposed) return;
    refreshing = true;
    try {
      const stored = readStored();
      const next = parse(stored.value);
      if (next && (!memoryOnly || !preference || Date.parse(next.decided_at) >= Date.parse(preference.decided_at))) {
        memoryOnly = false;
        publish(next);
      } else if (!validateConsent(preference, now()) || (!memoryOnly && stored.available)) {
        publish(null);
      }
    } finally { refreshing = false; }
  }

  function persist(next) {
    try {
      const value = JSON.stringify(next);
      if (local()) target.localStorage.setItem(CONSENT_STORAGE_KEY, value);
      else if (target?.location?.protocol === "https:" && /(^|\.)africaspocket\.com$/i.test(target.location.hostname)) {
        target.document.cookie = CONSENT_COOKIE + "=" + encodeURIComponent(value) +
          "; Domain=africaspocket.com; Path=/; Max-Age=" + CONSENT_MAX_AGE + "; SameSite=Lax; Secure";
      } else return false;
      return readStored().value === value;
    } catch { return false; }
  }

  function setDecision(decision) {
    if (!["granted", "denied"].includes(decision) || disposed) return;
    refresh();
    if (preference?.decision === decision) return;
    const next = { version: 1, notice_version: NOTICE_VERSION, decision, decided_at: new Date(now()).toISOString() };
    memoryOnly = !persist(next);
    publish(next);
    try { channel?.postMessage({ type: "preference", value: next }); } catch { /* Same-origin notification is optional. */ }
  }
  const onGrant = event => { if (event.detail?.notice_version === NOTICE_VERSION) setDecision("granted"); };
  const onWithdrawal = () => setDecision("denied");
  const onVisibility = () => { if (target?.document?.visibilityState !== "hidden") refresh(); };
  const onStorage = (event) => { if (event.key === CONSENT_STORAGE_KEY || event.key === null) refresh(); };
  target?.addEventListener?.("ap:attribution-consent-granted", onGrant);
  target?.addEventListener?.("ap:attribution-consent-withdrawn", onWithdrawal);
  target?.addEventListener?.("focus", refresh);
  target?.addEventListener?.("pageshow", refresh);
  target?.addEventListener?.("storage", onStorage);
  target?.document?.addEventListener?.("visibilitychange", onVisibility);
  try {
    channel = new target.BroadcastChannel("ap-traffic-consent-v1");
    channel.onmessage = (event) => {
      if (event.data?.type !== "preference") return;
      const next = validateConsent(event.data.value, now());
      if (!next || (preference && Date.parse(next.decided_at) < Date.parse(preference.decided_at))) return;
      memoryOnly = readStored().value !== JSON.stringify(next);
      publish(next);
    };
  } catch { /* Focus/read-before-submit still refresh shared state. */ }
  if (target) target.APAttributionConsentGranted = preference?.decision === "granted";

  return {
    hasConsent() { refresh(); return preference?.decision === "granted"; },
    getState() { refresh(); return preference?.decision || "unknown"; },
    getPreference() { refresh(); return preference ? { ...preference } : null; },
    setDecision, refresh,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    dispose() {
      disposed = true;
      target?.removeEventListener?.("ap:attribution-consent-granted", onGrant);
      target?.removeEventListener?.("ap:attribution-consent-withdrawn", onWithdrawal);
      target?.removeEventListener?.("focus", refresh);
      target?.removeEventListener?.("pageshow", refresh);
      target?.removeEventListener?.("storage", onStorage);
      target?.document?.removeEventListener?.("visibilitychange", onVisibility);
      channel?.close();
      listeners.clear();
    },
  };
}

export const attributionConsent = createConsentAdapter();
export const hasAttributionConsent = attributionConsent.hasConsent;
